/**
 * Stale-while-revalidate cache for GET /group/:id/getChat (page 1).
 *
 * Opening a chat used to wait on the network before any messages appeared.
 * This helper keeps the last page of messages in memory + localStorage so
 * Conversation / GroupConversation can paint immediately, then refresh.
 *
 * Page-1 network is skipped only when the cache is younger than FRESH_MS
 * (hover → open remount). Older persisted rows must still revalidate so
 * messages that arrived while the thread was closed are not dropped.
 *
 * Also de-dupes in-flight page-1 requests so a hover/list prefetch and the
 * subsequent open share a single round-trip.
 */

import { callAPI } from "./apiUtils";

const STORAGE_PREFIX = "kjuupChatMsgCache:v2:";
const MAX_CHATS = 25;
const MAX_MESSAGES = 40;
const FRESH_MS = 2500;
const PERSIST_DEBOUNCE_MS = 400;

const memory = new Map();
const inFlight = new Map();
// Page-1 keys that already completed this session. Remounts / isOpened
// flicker must not issue another /getChat until the chat is invalidated.
const page1Fetched = new Set();
let persistTimer = null;
let hydratedUserId = null;

function currentUserId() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    return parsed?.id ? String(parsed.id) : "";
  } catch {
    return "";
  }
}

function storageKey(userId) {
  return `${STORAGE_PREFIX}${userId || "anon"}`;
}

function normalizeLang(translationLang) {
  if (!translationLang) return "";
  return String(translationLang).split("-")[0].toLowerCase();
}

function cacheKey(chatId, translationLang) {
  return `${String(chatId)}::${normalizeLang(translationLang)}`;
}

function inflightKey(chatId, page, translationLang) {
  // Page-1 hover / open / remount must share one round-trip even when
  // callers disagree on translationLang casing ("en" vs "EN").
  if (Number(page) === 1) return `p1:${String(chatId)}`;
  return `${String(chatId)}::${page}::${normalizeLang(translationLang)}`;
}

function fingerprint(messages) {
  if (!Array.isArray(messages) || messages.length === 0) return "";
  const first = messages[0]?.id || "";
  const last = messages[messages.length - 1]?.id || "";
  return `${messages.length}:${first}:${last}`;
}

function ensureHydrated() {
  const userId = currentUserId();
  if (hydratedUserId === userId) return;
  if (hydratedUserId && hydratedUserId !== userId) {
    memory.clear();
  }
  hydratedUserId = userId;
  if (!userId) return;
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return;
    const parsed = JSON.parse(raw);
    const entries = Array.isArray(parsed?.entries) ? parsed.entries : [];
    for (const entry of entries) {
      if (!entry?.key || !Array.isArray(entry.rawMessages)) continue;
      memory.set(entry.key, {
        rawMessages: entry.rawMessages,
        secretKey: entry.secretKey || null,
        lastFetched: entry.lastFetched || 0,
        fingerprint: entry.fingerprint || fingerprint(entry.rawMessages),
      });
    }
  } catch {
    // Corrupt cache — ignore and start empty.
  }
}

function schedulePersist() {
  if (persistTimer) clearTimeout(persistTimer);
  persistTimer = setTimeout(flushPersist, PERSIST_DEBOUNCE_MS);
}

function flushPersist() {
  persistTimer = null;
  const userId = currentUserId();
  if (!userId) return;
  const entries = [...memory.entries()]
    .sort((a, b) => (b[1].lastFetched || 0) - (a[1].lastFetched || 0))
    .slice(0, MAX_CHATS)
    .map(([key, value]) => ({
      key,
      rawMessages: (value.rawMessages || []).slice(0, MAX_MESSAGES),
      secretKey: value.secretKey || null,
      lastFetched: value.lastFetched || 0,
      fingerprint: value.fingerprint || fingerprint(value.rawMessages),
    }));
  try {
    localStorage.setItem(storageKey(userId), JSON.stringify({ entries }));
  } catch {
    try {
      localStorage.setItem(
        storageKey(userId),
        JSON.stringify({ entries: entries.slice(0, 8) })
      );
    } catch {
      // Quota still exceeded — keep memory cache only.
    }
  }
}

export function getCachedChat(chatId, translationLang) {
  if (!chatId) return null;
  ensureHydrated();
  return memory.get(cacheKey(chatId, translationLang)) || null;
}

export function isChatCacheFresh(chatId, translationLang) {
  const cached = getCachedChat(chatId, translationLang);
  if (!cached?.rawMessages?.length) return false;
  // page1Fetched must not freeze a chat for the rest of the session —
  // messages that arrive while the thread is closed would never appear.
  return Date.now() - (cached.lastFetched || 0) < FRESH_MS;
}

export function cacheHasMessage(chatId, messageId, translationLang) {
  if (!chatId || !messageId) return false;
  const cached = getCachedChat(chatId, translationLang);
  const want = String(messageId);
  return (cached?.rawMessages || []).some(
    (m) => m && (String(m.id) === want || String(m.messageRequestId) === want)
  );
}

/** Last-message id from a Latest Messages / getUserChats row (`id` is the chat row). */
export function listPreviewMessageId(listRow) {
  if (!listRow) return null;
  const id = listRow.mainId || listRow.id;
  if (!id) return null;
  const groupId = listRow.group?.id || listRow.groupId;
  if (groupId && String(id) === String(groupId)) return null;
  return id;
}

export function isChatFetchInFlight(chatId, translationLang) {
  if (!chatId) return false;
  return inFlight.has(inflightKey(chatId, 1, translationLang))
    || inFlight.has(cacheKey(chatId, translationLang));
}

export function messagesFingerprint(messages) {
  return fingerprint(messages);
}

export function setCachedChat(chatId, { rawMessages, secretKey, translationLang } = {}) {
  if (!chatId || !Array.isArray(rawMessages)) return;
  ensureHydrated();
  const key = cacheKey(chatId, translationLang);
  const prev = memory.get(key) || {};
  memory.set(key, {
    rawMessages: rawMessages.slice(0, MAX_MESSAGES),
    secretKey: secretKey || prev.secretKey || null,
    lastFetched: Date.now(),
    fingerprint: fingerprint(rawMessages),
  });
  schedulePersist();
}

export function rememberChatSecret(chatId, secretKey, translationLang) {
  if (!chatId || !secretKey) return;
  ensureHydrated();
  const key = cacheKey(chatId, translationLang);
  const prev = memory.get(key);
  if (!prev) {
    memory.set(key, {
      rawMessages: [],
      secretKey,
      lastFetched: Date.now(),
      fingerprint: "",
    });
  } else if (prev.secretKey !== secretKey) {
    memory.set(key, { ...prev, secretKey });
  } else {
    return;
  }
  schedulePersist();
}

export function appendCachedMessage(chatId, message, translationLang) {
  // Only real message rows (createdAt or a messageType) — anything else would
  // paint as an empty bubble on the next open.
  if (!chatId || !message || typeof message !== "object" || !(message.createdAt || message.messageType)) return;
  ensureHydrated();
  const key = cacheKey(chatId, translationLang);
  const prev = memory.get(key);
  // Only patch an existing page-1 cache. Seeding a 1-message cache would
  // paint an incomplete thread and then skip the network as "fresh".
  if (!prev?.rawMessages?.length) return;
  const msgId = message.id || message.messageRequestId;
  const withoutDup = prev.rawMessages.filter((m) => {
    if (msgId && (m?.id === msgId || m?.messageRequestId === msgId)) return false;
    if (message.id && m?.id === message.id) return false;
    return true;
  });
  const rawMessages = [message, ...withoutDup].slice(0, MAX_MESSAGES);
  memory.set(key, {
    ...prev,
    rawMessages,
    lastFetched: Date.now(),
    fingerprint: fingerprint(rawMessages),
  });
  schedulePersist();
}

export function invalidateCachedChat(chatId) {
  if (!chatId) return;
  ensureHydrated();
  const id = String(chatId);
  const prefix = `${id}::`;
  for (const key of [...memory.keys()]) {
    if (key === id || key.startsWith(prefix)) {
      memory.delete(key);
    }
  }
  for (const key of [...page1Fetched]) {
    if (key === id || key.startsWith(prefix)) {
      page1Fetched.delete(key);
    }
  }
  for (const key of [...inFlight.keys()]) {
    if (key === id || key === `p1:${id}` || key.startsWith(prefix)) {
      inFlight.delete(key);
    }
  }
  schedulePersist();
}

export function clearChatMessageCache() {
  memory.clear();
  inFlight.clear();
  page1Fetched.clear();
  hydratedUserId = null;
  try {
    const toRemove = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(STORAGE_PREFIX)) toRemove.push(k);
    }
    toRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}

export function buildGetChatUrl({
  chatId,
  page = 1,
  limit = 30,
  userId,
  translationLang,
  building,
  viewByOwner,
}) {
  let url = `/group/${chatId}/getChat?page=${page}&limit=${limit}`;
  if (userId) url += `&userId=${userId}`;
  if (building) url += `&building=${building}`;
  if (viewByOwner !== undefined && viewByOwner !== null && viewByOwner !== "") {
    url += `&viewByOwner=${viewByOwner}`;
  }
  const lang = normalizeLang(translationLang);
  if (lang) url += `&translationLang=${lang}`;
  return url;
}

export function resolveListChatId(chat) {
  if (!chat) return null;
  return (
    chat.group?.id ||
    chat.mainId ||
    chat.id ||
    chat.key ||
    null
  );
}

function cachedPage1Response(cached) {
  return {
    data: {
      status: true,
      chatHistory: cached?.rawMessages || [],
      groupInfo: cached?.secretKey ? { secretKey: cached.secretKey } : {},
      chatEnd: !cached?.rawMessages?.length,
      fromCache: true,
    },
  };
}

/**
 * Fetch page 1 (or any page). Page-1 hits share in-flight requests and
 * populate the cache. A response younger than FRESH_MS is reused (hover /
 * remount). `force` always hits the network. `expectMessageId` forces a
 * refetch when the list preview is ahead of the cached thread.
 */
export function fetchChatPage({
  chatId,
  page = 1,
  limit = 30,
  userId,
  translationLang,
  building,
  viewByOwner,
  force = false,
  expectMessageId,
}) {
  if (!chatId) {
    return Promise.resolve({ data: { status: false } });
  }

  const key = cacheKey(chatId, translationLang);
  const flightKey = inflightKey(chatId, page, translationLang);
  if (page === 1 && !force) {
    const existing = inFlight.get(flightKey) || inFlight.get(key);
    if (existing) return existing;

    const cached = getCachedChat(chatId, translationLang);
    const cacheIsFresh =
      cached?.rawMessages?.length &&
      Date.now() - (cached.lastFetched || 0) < FRESH_MS;
    const hasExpected =
      !expectMessageId || cacheHasMessage(chatId, expectMessageId, translationLang);
    // Reuse only a just-fetched page (hover/open remount). Persisted
    // localStorage rows must not block the network for the rest of the session.
    if (cacheIsFresh && hasExpected) {
      return Promise.resolve(cachedPage1Response(cached));
    }
  }

  const url = buildGetChatUrl({
    chatId,
    page,
    limit,
    userId,
    translationLang,
    building,
    viewByOwner,
  });

  const request = callAPI(url, {}, "GET")
    .then((response) => {
      const data = response?.data;
      if (page === 1 && data?.status) {
        page1Fetched.add(key);
        if (Array.isArray(data.chatHistory)) {
          setCachedChat(chatId, {
            rawMessages: data.chatHistory,
            secretKey: data.groupInfo?.secretKey,
            translationLang,
          });
        }
      }
      return response;
    })
    .finally(() => {
      if (page === 1) inFlight.delete(flightKey);
    });

  if (page === 1) inFlight.set(flightKey, request);
  return request;
}

export function prefetchChatMessages(params) {
  const chatId = params?.chatId;
  if (!chatId) return Promise.resolve(null);
  if (getCachedChat(chatId, params.translationLang)?.rawMessages?.length) {
    return Promise.resolve(getCachedChat(chatId, params.translationLang));
  }
  return fetchChatPage({ ...params, page: 1, force: false }).catch(() => null);
}

/**
 * Warm page-1 caches for the chats the user is most likely to open next.
 * Runs sequentially with a short gap so we don't starve the list render.
 */
export function prefetchChatList(chats, { userId, translationLang, limit = 8 } = {}) {
  if (!Array.isArray(chats) || chats.length === 0) return;
  const jobs = [];
  for (const chat of chats) {
    if (jobs.length >= limit) break;
    const chatId = resolveListChatId(chat);
    if (!chatId) continue;
    if (getCachedChat(chatId, translationLang)?.rawMessages?.length) continue;
    const isDirect = !!(chat.group?.isOneToOne || chat.isOneToOne);
    jobs.push({
      chatId,
      userId,
      translationLang,
      building: isDirect ? undefined : (chat.building || chat.buildingList || chat.group?.building),
    });
  }
  if (jobs.length === 0) return;

  const run = (index) => {
    if (index >= jobs.length) return;
    prefetchChatMessages(jobs[index]).finally(() => {
      setTimeout(() => run(index + 1), 180);
    });
  };

  const start = () => run(0);
  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(start, { timeout: 1800 });
  } else {
    setTimeout(start, 350);
  }
}
